import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { passwordPolicyError, PASSWORD_HELP_TEXT } from "../lib/passwordPolicy";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { KeyRound, Mail } from "lucide-react";

// Segurança Pré-Produção, Pacote 6 (2FA para redefinição administrativa de
// senha pelo Admin Master) — substitui o antigo modal de etapa única
// (nova senha + confirmação -> pronto) pelo MESMO padrão de duas etapas do
// Pacote 5, aplicado à ação do Admin Master sobre a conta de OUTRO
// usuário (administrador principal de uma empresa cliente):
//
// ETAPA 1 (POST /admin/companies/{cid}/reset-admin-password/request-code):
// senha ATUAL do próprio Admin Master + nova senha do usuário-alvo — o
// backend confirma a senha do Admin Master, valida a política da nova
// senha e envia um código de 6 dígitos para o e-mail de AUTORIZAÇÃO DE
// SEGURANÇA da própria Codexpy (nunca para o cliente — o backend resolve
// esse destinatário sozinho, de uma configuração global da plataforma).
// password_hash do alvo ainda NÃO muda.
// ETAPA 2 (POST /admin/companies/{cid}/reset-admin-password/confirm):
// reenvia os mesmos três campos + o código — o backend revalida tudo do
// zero antes de mudar a senha do alvo (a nova senha nunca é persistida em
// lugar nenhum entre as duas etapas; este componente a mantém só em
// memória/estado do React). Sucesso invalida as sessões do usuário-alvo
// (token_version) mas NUNCA toca na sessão do próprio Admin Master.
export default function AdminResetClientPasswordDialog({ company, open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1); // 1=solicitar, 2=confirmar
  const [masterPw, setMasterPw] = useState("");
  const [nw, setNw] = useState("");
  const [confirmNw, setConfirmNw] = useState("");
  const [code, setCode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const reset = () => {
    setStep(1); setMasterPw(""); setNw(""); setConfirmNw(""); setCode(""); setMaskedEmail(""); setCooldown(0);
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestBody = () => ({
    current_admin_master_password: masterPw, new_password: nw, confirm_password: confirmNw,
  });

  const requestCode = async () => {
    const pwErr = passwordPolicyError(nw);
    if (pwErr) { toast.error(pwErr); return; }
    if (nw !== confirmNw) { toast.error("Senhas não conferem"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/reset-admin-password/request-code`, requestBody());
      setMaskedEmail(data.masked_email || "");
      setCooldown(data.resend_cooldown_sec || 60);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/reset-admin-password/request-code`, requestBody());
      setMaskedEmail(data.masked_email || "");
      setCooldown(data.resend_cooldown_sec || 60);
      toast.success("Novo código enviado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Informe os 6 dígitos do código."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/companies/${company.id}/reset-admin-password/confirm`, { ...requestBody(), code });
      toast.success("Senha do administrador redefinida");
      onOpenChange(false);
      reset();
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancel = () => { onOpenChange(false); reset(); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) cancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Redefinir senha — {company?.primary_admin_name || company?.razao_social}</DialogTitle>
        </DialogHeader>
        {step === 1 && (
          <>
            <div className="space-y-3">
              <div>
                <Label>Nova senha</Label>
                <Input type="password" data-testid="empresa-pw-nova" value={nw} onChange={e => setNw(e.target.value)} />
                <p className="text-xs text-slate-400 mt-1">{PASSWORD_HELP_TEXT}</p>
              </div>
              <div>
                <Label>Confirmação da nova senha</Label>
                <Input type="password" data-testid="empresa-pw-confirma" value={confirmNw} onChange={e => setConfirmNw(e.target.value)} />
              </div>
              <div className="pt-2 border-t border-slate-100">
                <Label>Sua senha atual (Admin Master)</Label>
                <Input type="password" data-testid="empresa-pw-master-atual" value={masterPw} onChange={e => setMasterPw(e.target.value)} autoComplete="current-password" />
                <p className="text-xs text-slate-400 mt-1">
                  Confirmamos sua identidade e enviamos um código de autorização por e-mail antes de redefinir a senha do cliente.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={cancel}>Cancelar</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={requestCode} disabled={busy || !nw || !confirmNw || !masterPw} data-testid="empresa-pw-enviar-codigo">
                <Mail size={16} /> {busy ? "Enviando..." : "Enviar código de autorização"}
              </Button>
            </DialogFooter>
          </>
        )}
        {step === 2 && (
          <>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Enviamos um código de autorização para{" "}
                <span className="font-medium text-slate-900">{maskedEmail}</span>.
              </p>
              <div>
                <Label>Código de 6 dígitos</Label>
                <Input
                  inputMode="numeric" pattern="\d{6}" maxLength={6}
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoFocus className="text-center text-2xl tracking-[0.5em]" placeholder="000000"
                  data-testid="empresa-pw-codigo"
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={resend} disabled={cooldown > 0 || busy} className="text-blue-600 hover:underline disabled:text-slate-300 disabled:no-underline" data-testid="empresa-pw-reenviar-codigo">
                  {cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={cancel}>Cancelar</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={confirm} disabled={busy || code.length !== 6} data-testid="empresa-pw-salvar">
                <KeyRound size={16} /> {busy ? "Confirmando..." : "Confirmar redefinição"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
